import type { BonusTextFile } from '../types/domain';

interface LyricTemplateSeed {
  fileName: string;
  title: string;
  genre: string;
  mood: string;
  tempo: string;
  coreIdea: string;
  hookDirection: string;
  verseArc: string;
  contrastSection: string;
  imagery: string;
  craftDirection: string;
  structure: string;
}

function template(
  fileName: string,
  title: string,
  genre: string,
  mood: string,
  tempo: string,
  coreIdea: string,
  hookDirection: string,
  verseArc: string,
  contrastSection: string,
  imagery: string,
  craftDirection: string,
  structure: string
): LyricTemplateSeed {
  return {
    fileName,
    title,
    genre,
    mood,
    tempo,
    coreIdea,
    hookDirection,
    verseArc,
    contrastSection,
    imagery,
    craftDirection,
    structure,
  };
}

const LYRIC_TEMPLATE_SEEDS: readonly LyricTemplateSeed[] = [
  template(
    '01-acoustic-pop-coffee-shop-confession.txt',
    'Coffee Shop Confession',
    'Acoustic pop',
    'Intimate, hopeful, conversational',
    '88-96 BPM',
    'Someone rehearses an honest confession while waiting for a familiar person to arrive.',
    'Build the chorus around one plainspoken sentence the singer has never managed to say aloud.',
    'Verse 1 notices small details in the room; verse 2 imagines what could change after the truth is spoken.',
    'In the bridge, let courage briefly fail before the singer chooses honesty.',
    'Cooling coffee, a fogged window, an empty chair, a name written on a paper cup.',
    'Use natural speech, gentle internal rhyme, and one repeated image that gains meaning in each section.',
    'Intro - Verse 1 - Pre-Chorus - Chorus - Verse 2 - Chorus - Bridge - Final Chorus - Outro'
  ),
  template(
    '02-acoustic-pop-open-road.txt',
    'Open Road, Open Hands',
    'Acoustic pop',
    'Free, warm, quietly triumphant',
    '100-108 BPM',
    'Leaving a familiar place without bitterness and trusting the life waiting beyond it.',
    'Center the hook on releasing the past rather than running away from it.',
    'Verse 1 packs the final box; verse 2 crosses a county line and notices the first sign of possibility.',
    'The bridge names one thing the singer will miss and one thing they will never miss again.',
    'Dashboard sunlight, rolled-down windows, a fading town sign, dust in the rearview mirror.',
    'Keep lines singable over strummed guitar; favor open vowels and clean end rhymes in the chorus.',
    'Verse 1 - Chorus - Verse 2 - Chorus - Bridge - Half Chorus - Final Chorus'
  ),
  template(
    '03-synthwave-neon-reunion.txt',
    'Neon Reunion',
    'Synthwave',
    'Nostalgic, electric, romantic',
    '104-112 BPM',
    'Two former lovers unexpectedly meet in the city that once belonged to them.',
    'Make the title phrase land like a memory switching back on.',
    'Verse 1 recognizes the face across the street; verse 2 tests whether the old chemistry survived.',
    'Drop into a stripped bridge where the singer admits memory may be brighter than reality.',
    'Wet pavement, motel signs, cassette hiss, taillights, violet midnight.',
    'Use cinematic short lines, selective 1980s imagery, and a chorus with a long sustained final word.',
    'Atmospheric Intro - Verse 1 - Pre-Chorus - Chorus - Verse 2 - Chorus - Synth Break - Bridge - Final Chorus'
  ),
  template(
    '04-synthwave-midnight-escape.txt',
    'Midnight Escape',
    'Synthwave',
    'Urgent, defiant, euphoric',
    '118-126 BPM',
    'A pair of outsiders leave a controlled life behind before sunrise.',
    'Write a two-line call-and-response hook about choosing motion over permission.',
    'Verse 1 starts the getaway; verse 2 reveals what each person risked by leaving.',
    'The bridge freezes the action for one honest question: what happens when morning comes?',
    'Street grids, red warning lights, engine heat, radio static, a pale horizon.',
    'Use clipped phrases in verses and wider, vowel-heavy lines in the chorus to create lift.',
    'Intro - Verse 1 - Pre-Chorus - Chorus - Verse 2 - Pre-Chorus - Chorus - Instrumental Break - Bridge - Double Chorus'
  ),
  template(
    '05-lofi-instrumental-memory-loop.txt',
    'Memory Loop',
    'Lo-fi instrumental',
    'Reflective, dusty, bittersweet',
    '72-82 BPM',
    'Sparse vocal fragments surface like incomplete memories inside an instrumental beat.',
    'Use a six-to-nine-word refrain that still makes sense when chopped, reversed, or repeated.',
    'Instead of full verses, write four standalone image lines that can appear between instrumental passages.',
    'Add one spoken bridge sentence that changes the meaning of the refrain.',
    'Vinyl dust, an old voicemail, late buses, notebook margins, rain on glass.',
    'Leave deliberate silence; avoid narrative overload and make every phrase usable as a sample.',
    'Instrumental Intro - Vocal Fragment A - Beat Passage - Refrain - Beat Passage - Spoken Bridge - Refrain - Outro'
  ),
  template(
    '06-cinematic-last-light.txt',
    'The Last Light',
    'Cinematic',
    'Solemn, expansive, resilient',
    '70-84 BPM with a gradual build',
    'A community holds onto hope during its darkest hour.',
    'Shape the chorus as a collective vow beginning quietly and ending with certainty.',
    'Verse 1 shows the loss; verse 2 reveals ordinary people choosing to rebuild.',
    'The bridge should reduce to a single voice before the final choir enters.',
    'Cold stone, distant fires, joined hands, dawn through smoke, a bell across a valley.',
    'Move from concrete details to universal language; reserve the largest words for the final chorus.',
    'Orchestral Intro - Verse 1 - Lift - Chorus - Verse 2 - Chorus - Bridge - Choir Refrain - Final Chorus - Coda'
  ),
  template(
    '07-cinematic-rising-kingdom.txt',
    'When the Kingdom Rises',
    'Cinematic',
    'Epic, determined, victorious',
    '92-104 BPM',
    'A fallen leader returns, not for revenge, but to restore what fear dismantled.',
    'Build the hook around the people rising together rather than one hero claiming power.',
    'Verse 1 remembers the fall; verse 2 gathers voices and turns private courage into a movement.',
    'Use the bridge for a calm promise made immediately before the final surge.',
    'Broken banners, mountain paths, drums beyond the walls, first light on metal.',
    'Use strong stresses, parallel phrases, and controlled repetition suitable for a large ensemble.',
    'Chant Intro - Verse 1 - Build - Chorus - Verse 2 - Build - Chorus - Quiet Bridge - Final Chorus - Chant Outro'
  ),
  template(
    '08-hip-hop-self-made.txt',
    'Receipts',
    'Hip hop',
    'Confident, focused, celebratory',
    '88-96 BPM',
    'Success is documented through sacrifices and habits, not empty boasting.',
    'Make the hook a memorable inventory of proof: time, work, setbacks, and results.',
    'Verse 1 covers the unseen routine; verse 2 contrasts old doubt with present discipline.',
    'Switch flow in the final eight bars and acknowledge the people who helped.',
    'Early alarms, worn notebooks, missed calls, train platforms, signed paperwork.',
    'Use multisyllabic rhyme in four-bar pockets; keep claims specific enough to feel earned.',
    'Cold Open - Verse 1 (16 bars) - Hook (8 bars) - Verse 2 (16 bars) - Hook - Flow Switch (8 bars) - Final Hook'
  ),
  template(
    '09-hip-hop-city-testimony.txt',
    'City Testimony',
    'Hip hop',
    'Observant, grounded, socially conscious',
    '82-90 BPM',
    'One city block is described through the intersecting lives of its residents.',
    'Use the hook to ask who gets remembered when a neighborhood changes.',
    'Verse 1 follows morning workers; verse 2 follows young dreamers; verse 3 returns after visible change.',
    'A short spoken interlude should quote an original line from an elder in the community.',
    'Corner shops, bus brakes, scaffolding, school uniforms, fresh paint over old names.',
    'Favor internal rhyme and scene-based writing; avoid sermons by letting details carry the argument.',
    'Scene Intro - Verse 1 - Hook - Verse 2 - Hook - Spoken Interlude - Verse 3 - Final Hook - Outro'
  ),
  template(
    '10-afrobeats-summer-promise.txt',
    'Promise in the Sun',
    'Afrobeats',
    'Flirtatious, bright, sincere',
    '102-110 BPM',
    'A playful summer connection begins to feel more lasting than either person expected.',
    'Create a compact, repeatable hook that pairs a promise with a physical dance cue.',
    'Verse 1 is teasing and light; verse 2 becomes direct about wanting something real.',
    'In the bridge, strip away the bravado and ask for a clear answer.',
    'Golden-hour skin, rooftop breeze, crowded streets, sandals on warm concrete.',
    'Use syncopated phrases, open vowels, and tasteful repetition; leave pockets for percussion.',
    'Intro Chant - Verse 1 - Pre-Chorus - Chorus - Post-Chorus - Verse 2 - Chorus - Bridge - Final Chorus'
  ),
  template(
    '11-afrobeats-homecoming.txt',
    'Road Back Home',
    'Afrobeats',
    'Joyful, grateful, communal',
    '108-116 BPM',
    'Returning home after years away and discovering belonging has changed but not disappeared.',
    'Let the chorus name home through people and sounds rather than geography alone.',
    'Verse 1 captures arrival; verse 2 reconnects with family, friends, and a younger self.',
    'Use a call-and-response bridge that invites a group vocal.',
    'Market voices, cooking smoke, familiar slang, evening generators, courtyard dancing.',
    'Blend conversational lines with a chantable refrain; keep cultural details precise and respectful.',
    'Percussion Intro - Verse 1 - Chorus - Post-Chorus - Verse 2 - Chorus - Call-and-Response Bridge - Double Chorus'
  ),
  template(
    '12-rock-break-the-silence.txt',
    'Break the Silence',
    'Rock',
    'Restless, confrontational, liberating',
    '128-142 BPM',
    'Someone finally challenges a relationship built on avoidance and half-truths.',
    'Make the title the final command in a chorus that escalates line by line.',
    'Verse 1 catalogs evasions; verse 2 states the cost of letting them continue.',
    'The bridge should turn inward and admit the singer also helped maintain the silence.',
    'Slamming doors, a muted television, cracked plaster, ringing ears, an open window.',
    'Use hard consonants and short lines over the riff; broaden the final chorus with backing vocals.',
    'Riff Intro - Verse 1 - Pre-Chorus - Chorus - Verse 2 - Chorus - Guitar Break - Bridge - Final Chorus'
  ),
  template(
    '13-rock-after-the-fire.txt',
    'After the Fire',
    'Rock',
    'Raw, resilient, anthemic',
    '112-124 BPM',
    'Rebuilding identity after a public failure.',
    'Anchor the chorus in what remains after reputation and certainty burn away.',
    'Verse 1 faces the wreckage; verse 2 identifies one stubborn reason to begin again.',
    'Use a half-time bridge where the singer stops defending the past.',
    'Ash on photographs, exposed beams, blistered hands, sparks under rain.',
    'Balance grit with melody; repeat one key noun while changing the verb around it.',
    'Intro - Verse 1 - Chorus - Verse 2 - Chorus - Half-Time Bridge - Solo - Final Chorus - Outro'
  ),
  template(
    '14-pop-magnetic.txt',
    'Magnetic',
    'Pop',
    'Playful, polished, irresistible',
    '116-124 BPM',
    'Two people keep finding their way back together despite sensible reasons not to.',
    'Write a title-centered hook with one instantly repeatable setup line and one payoff line.',
    'Verse 1 stages an accidental meeting; verse 2 shows the pattern is no longer accidental.',
    'The bridge should question whether attraction is fate, habit, or a choice.',
    'Train doors, crossed signals, mirrored elevators, compass needles, static touch.',
    'Keep the chorus concise, use fresh magnetic imagery sparingly, and avoid filler syllables.',
    'Intro - Verse 1 - Pre-Chorus - Chorus - Post-Chorus - Verse 2 - Pre-Chorus - Chorus - Bridge - Final Chorus'
  ),
  template(
    '15-pop-new-chapter.txt',
    'Brand New Page',
    'Pop',
    'Optimistic, youthful, forward-looking',
    '108-118 BPM',
    'A fresh start feels possible after finally closing an exhausting chapter.',
    'Make the hook celebrate authorship: the singer chooses what gets written next.',
    'Verse 1 closes out old habits; verse 2 tries one brave action in the new life.',
    'Use the bridge to admit blank pages are exciting and frightening at once.',
    'Ink-stained fingers, torn calendars, morning light, an unlocked front door.',
    'Use simple images with one clever lyrical turn; each chorus should add a new final line.',
    'Verse 1 - Pre-Chorus - Chorus - Verse 2 - Pre-Chorus - Chorus - Bridge - Final Chorus - Tag'
  ),
  template(
    '16-rnb-late-night-truth.txt',
    'After Two',
    'R&B',
    'Vulnerable, sensual, uncertain',
    '68-76 BPM',
    'Late-night honesty exposes feelings that daytime conversation keeps hidden.',
    'Build the hook around the hour when defenses disappear.',
    'Verse 1 reads between messages; verse 2 asks whether intimacy survives in daylight.',
    "The bridge should contain the song's most direct question with no metaphor around it.",
    'Phone glow, quiet traffic, tangled sheets, melting ice, curtains before dawn.',
    'Use conversational runs, internal echoes, and space at line endings for vocal improvisation.',
    'Ambient Intro - Verse 1 - Pre-Chorus - Chorus - Verse 2 - Chorus - Vocal Bridge - Final Chorus - Ad-libs'
  ),
  template(
    '17-rnb-slow-burn.txt',
    'Slow Burn',
    'R&B',
    'Patient, romantic, assured',
    '76-86 BPM',
    'A relationship grows through consistency rather than instant drama.',
    'Let the chorus contrast fireworks with a warmth that lasts.',
    'Verse 1 notices reliable gestures; verse 2 recognizes trust replacing old anxiety.',
    'In the bridge, make a future promise without making it grandiose.',
    'Porch lights, simmering food, folded laundry, Sunday rain, a hand at the small of the back.',
    'Favor tactile language, near rhymes, and melodic phrases that can stretch across the bar line.',
    'Intro - Verse 1 - Chorus - Verse 2 - Chorus - Bridge - Breakdown Chorus - Final Chorus'
  ),
  template(
    '18-country-small-town-letter.txt',
    'Letter to Main Street',
    'Country',
    'Nostalgic, affectionate, honest',
    '92-104 BPM',
    'A letter to a hometown acknowledges both the comfort it gave and the limits that made leaving necessary.',
    'Address the town directly in a chorus that holds gratitude and distance together.',
    'Verse 1 recalls landmarks; verse 2 tells what the singer learned after leaving.',
    'The bridge imagines returning as a visitor and seeing the place clearly.',
    'A courthouse clock, football lights, a two-lane road, screen doors, a boarded cinema.',
    'Use concrete nouns, clean storytelling, and one conversational twist at the end of each verse.',
    'Verse 1 - Chorus - Verse 2 - Chorus - Bridge - Final Chorus - Instrumental Tag'
  ),
  template(
    '19-country-last-train-home.txt',
    'Last Train Home',
    'Country',
    'Regretful, tender, determined',
    '78-90 BPM',
    'After choosing ambition over love, the singer takes one final chance to return.',
    'Make the train both a literal deadline and a metaphor for a closing opportunity.',
    'Verse 1 reaches the station; verse 2 remembers the exact goodbye that caused the separation.',
    'The bridge happens as the doors close, forcing a decision in real time.',
    'Platform rain, a paper ticket, station announcements, suitcase wheels, porch light.',
    'Keep chronology clear, rhyme unobtrusively, and save the emotional reveal for the chorus payoff.',
    'Story Intro - Verse 1 - Chorus - Verse 2 - Chorus - Bridge - Final Chorus - Resolution Outro'
  ),
  template(
    '20-jazz-after-hours.txt',
    'After Hours',
    'Jazz',
    'Smoky, witty, sophisticated',
    '92-116 BPM swing',
    'Two strangers trade guarded observations as a nearly empty venue closes.',
    'Use a sly recurring phrase whose meaning shifts from small talk to invitation.',
    'Verse 1 studies the room; verse 2 turns the observations toward the other person.',
    'Write an eight-line middle section with longer phrases that briefly abandon the rhyme scheme.',
    'Stacked chairs, lipstick on a glass, blue stage light, a tired pianist, keys at the door.',
    'Use conversational syncopation, elegant slant rhyme, and room for instrumental answers.',
    'Instrumental Head - Verse 1 - Refrain - Verse 2 - Refrain - Middle Eight - Solo - Final Refrain - Tag'
  ),
  template(
    '21-jazz-second-chance.txt',
    'One More Measure',
    'Jazz',
    'Romantic, rueful, lightly playful',
    '72-88 BPM ballad',
    'A musician asks for one more chance using musical time as the central metaphor.',
    'The refrain should ask for one more measure without promising a perfect performance.',
    'Verse 1 admits where the singer rushed; verse 2 explains what listening has taught them.',
    'The middle eight drops the extended metaphor for one plain apology.',
    'Marked sheet music, a missed entrance, a held note, an empty bandstand.',
    'Avoid overloading musical puns; use sophisticated rhyme and let one line land behind the beat.',
    'Rubato Intro - Verse 1 - Refrain - Verse 2 - Refrain - Middle Eight - Solo - Final Refrain'
  ),
  template(
    '22-blues-empty-pocket.txt',
    'Empty Pocket Blues',
    'Blues',
    'Wry, weary, resilient',
    '78-92 BPM shuffle',
    'Financial trouble is met with humor, resourcefulness, and stubborn dignity.',
    'Build a repeatable AAB refrain around an empty pocket and a head still held high.',
    'Verse 1 lists practical setbacks; verse 2 finds comic relief; verse 3 names what money cannot take.',
    'Use a stop-time verse where each setup waits for an instrumental answer.',
    'A final coin, overdue notices, worn soles, beans on the stove, morning work boots.',
    'Follow an AAB blues shape while keeping each third line surprising and specific.',
    'Turnaround - Verse 1 - Verse 2 - Refrain - Stop-Time Verse - Solo - Verse 3 - Final Refrain'
  ),
  template(
    '23-blues-rainy-morning.txt',
    'Rain Before Breakfast',
    'Blues',
    'Lonely, earthy, dryly humorous',
    '66-78 BPM',
    'A bad morning becomes the final proof that a relationship is truly over.',
    'Use breakfast and weather imagery for a refrain that sounds resigned, not self-pitying.',
    'Verse 1 wakes to absence; verse 2 discovers a small object left behind; verse 3 chooses to move on.',
    'The instrumental break should be framed by two short spoken lines.',
    'Cold coffee, leaking gutters, one clean plate, a silent radio, muddy steps.',
    'Keep lines plain and singable; use repetition with one changed word to move the story forward.',
    'Guitar Intro - Verse 1 - Refrain - Verse 2 - Refrain - Spoken Break - Solo - Verse 3 - Refrain'
  ),
  template(
    '24-classical-vocal-requiem-for-fear.txt',
    'Requiem for Fear',
    'Classical',
    'Reverent, searching, transcendent',
    'Free tempo into 64-72 BPM',
    'A formal farewell is offered not to a person, but to the fear that governed a life.',
    'Create a recurring invocation that can be sung by soloist and answered by choir.',
    'The first movement names fear; the second recalls its cost; the third imagines life after release.',
    'Use a quiet solo passage as the human center before the final choral resolution.',
    'Candle smoke, vaulted stone, winter branches, opening doors, breath becoming song.',
    'Favor timeless language, measured repetition, and vowel-rich phrases suitable for sustained notes.',
    'Instrumental Prelude - Solo Invocation - Choral Response - Movement II - Interlude - Solo Passage - Final Chorus - Amen Coda'
  ),
  template(
    '25-reggae-better-days.txt',
    'Better Days Growing',
    'Reggae',
    'Steady, hopeful, socially aware',
    '76-88 BPM',
    'Hope is treated as daily work planted in family and community.',
    'Shape the chorus around planting today what the next generation will live beneath.',
    'Verse 1 names present pressure; verse 2 shows neighbors creating practical change.',
    'Use a group-response bridge that turns the central image into a shared commitment.',
    'Seedlings through concrete, school gates, shared meals, morning radios, shade trees.',
    'Use offbeat-friendly phrasing, grounded language, and repetition that deepens rather than stalls.',
    'Intro - Verse 1 - Chorus - Dub Space - Verse 2 - Chorus - Call-and-Response Bridge - Final Chorus - Dub Outro'
  ),
  template(
    '26-soul-standing-tall.txt',
    'Still Standing Tall',
    'Soul',
    'Defiant, warm, deeply human',
    '86-98 BPM',
    'The singer takes stock of hardship without allowing it to define their worth.',
    'Build a rising chorus where each line names something pressure could not take away.',
    'Verse 1 recounts a low point; verse 2 recognizes inherited strength and present support.',
    'The bridge begins as a whisper and becomes a testimony for the final chorus.',
    'Scuffed shoes, family photographs, kitchen-table prayers, sunlight through blinds.',
    'Use gospel-informed repetition, vivid testimony, and room for melisma on the hook title.',
    'Organ Intro - Verse 1 - Pre-Chorus - Chorus - Verse 2 - Chorus - Testimony Bridge - Vamp - Final Chorus'
  ),
  template(
    '27-soul-tender-mercy.txt',
    'Tender Mercy',
    'Soul',
    'Compassionate, remorseful, restorative',
    '72-82 BPM',
    'Someone asks for forgiveness while accepting that reconciliation cannot be demanded.',
    'Let the chorus request mercy without bargaining or shifting blame.',
    'Verse 1 names the harm plainly; verse 2 describes changed behavior rather than promises alone.',
    'In the bridge, allow the other person complete freedom to walk away.',
    'An unanswered doorway, folded letters, tired hands, evening church bells.',
    'Choose emotionally exact verbs, restrained rhyme, and a final chorus that changes one crucial pronoun.',
    'Piano Intro - Verse 1 - Chorus - Verse 2 - Chorus - Bridge - Vocal Vamp - Final Chorus'
  ),
  template(
    '28-gospel-grace-found-me.txt',
    'Grace Found Me',
    'Gospel',
    'Grateful, redemptive, uplifting',
    '84-98 BPM',
    'A personal testimony traces the distance between isolation and restored faith.',
    'Write a choir-ready refrain where grace acts before the singer knows how to ask.',
    'Verse 1 shows the lowest room; verse 2 marks the first sign of change; verse 3 lives in the aftermath.',
    'Use a leader-and-choir bridge that names several forms of restoration.',
    'A locked room, dawn under a door, open hands, a crowded aisle, lifted faces.',
    'Keep testimony specific, use cumulative repetition, and leave clear spaces for choir responses.',
    'Piano Intro - Verse 1 - Chorus - Verse 2 - Chorus - Testimony Verse - Call-and-Response Bridge - Vamp - Final Chorus'
  ),
  template(
    '29-gospel-victory-morning.txt',
    'Victory Morning',
    'Gospel',
    'Joyful, communal, triumphant',
    '104-118 BPM',
    'A congregation celebrates making it through a long season of uncertainty.',
    'Make the chorus a simple declaration that can expand through repeated choir harmonies.',
    'Verse 1 remembers waiting; verse 2 recognizes signs of breakthrough in ordinary life.',
    'The bridge should become a sequence of short lead lines answered by the choir.',
    'Sunday shoes, ringing tambourines, sunrise windows, embraces in the aisle.',
    'Use accessible language, escalating repetition, and a modulation-ready final refrain.',
    'Choir Intro - Verse 1 - Chorus - Verse 2 - Chorus - Praise Break - Call-and-Response - Key Change - Final Chorus'
  ),
  template(
    '30-amapiano-weekend-pulse.txt',
    'Weekend Pulse',
    'Amapiano',
    'Social, confident, carefree',
    '110-114 BPM',
    'Friends step out after a demanding week and reclaim their energy together.',
    'Use a short rhythmic phrase that can cycle over the log-drum drop without crowding it.',
    'Verse 1 leaves work behind; verse 2 notices the whole room moving as one.',
    'Reduce the bridge to a leader phrase, group answer, and space for the groove.',
    'Fresh clothes, taxi lights, balcony air, bass through the floor, sunrise plans.',
    'Keep syllables per line low, use repetition strategically, and prioritize percussive vowel sounds.',
    'Atmospheric Intro - Verse 1 - Chant - Log-Drum Drop - Verse 2 - Chant - Breakdown - Final Drop - Outro'
  ),
  template(
    '31-amapiano-soft-life.txt',
    'Soft Life Sunday',
    'Amapiano',
    'Easygoing, luxurious, affectionate',
    '108-112 BPM',
    'A quiet day with someone special feels richer than any public display.',
    'Center the hook on choosing peace, time, and good company.',
    'Verse 1 slows the morning down; verse 2 turns ordinary comforts into a private celebration.',
    'Use a whispered bridge that invites the groove back in one element at a time.',
    'White curtains, cold fruit, bare feet, garden shade, phones turned face down.',
    'Write compact melodic phrases with gentle code-switching only if authentic to the writer.',
    'Intro - Verse 1 - Hook - Groove Passage - Verse 2 - Hook - Whispered Bridge - Log-Drum Drop - Final Hook'
  ),
  template(
    '32-edm-festival-unity.txt',
    'All as One',
    'EDM',
    'Euphoric, inclusive, explosive',
    '126-132 BPM',
    'Strangers in a festival crowd experience one temporary, borderless community.',
    'Write a maximum eight-word hook designed to survive repetition before and after the drop.',
    'Verse 1 enters alone; verse 2 recognizes shared emotion in unfamiliar faces.',
    'The breakdown should include four ascending lines that move from one voice to everyone.',
    'Wristbands, raised hands, dust under lasers, flags, first light beyond the stage.',
    'Use universal but concrete language; avoid generic party lists and leave room for production.',
    'Filtered Intro - Verse 1 - Build - Hook - Drop - Verse 2 - Build - Hook - Drop - Breakdown - Final Drop'
  ),
  template(
    '33-edm-afterglow.txt',
    'Afterglow',
    'EDM',
    'Emotional, soaring, bittersweet',
    '122-128 BPM',
    'A relationship has ended, but its emotional light still colors everything.',
    'Pair the title with one sensory detail that returns before each drop.',
    'Verse 1 walks through changed routines; verse 2 accepts that remembering is not the same as returning.',
    "Use the breakdown for the song's longest lyrical sentence, then cut to a minimal hook.",
    'Sunset through high windows, fading perfume, empty passenger seat, colored shadows.',
    'Keep verses intimate and the hook broad; choose open vowels that can be sustained and sampled.',
    'Intro - Verse 1 - Pre-Chorus - Hook - Drop - Verse 2 - Hook - Drop - Breakdown - Final Hook - Outro'
  ),
  template(
    '34-house-all-night.txt',
    'Room for the Night',
    'House',
    'Hypnotic, welcoming, sensual',
    '120-126 BPM',
    'The dance floor becomes a temporary refuge where everyone can arrive as they are.',
    'Create a warm four-line refrain that works over repeated chord cycles.',
    'Use two compact verses: one describes entering guarded, the other describes opening up.',
    'The breakdown repeats one invitation while harmonies and ad-libs accumulate.',
    'Coat-check tickets, mirrored walls, low ceilings, sweat, colored light on faces.',
    'Favor repetition with subtle variation, house-friendly phrasing, and no dense storytelling during drops.',
    'DJ Intro - Verse 1 - Refrain - Groove - Verse 2 - Refrain - Breakdown - Peak Groove - Final Refrain'
  ),
  template(
    '35-latin-burning-heart.txt',
    'Corazon en Llamas',
    'Latin',
    'Passionate, dramatic, danceable',
    '96-112 BPM',
    'Attraction and pride keep two people circling each other without resolution.',
    'Make the bilingual-ready hook easy to understand in either language and centered on the burning-heart image.',
    'Verse 1 issues a challenge; verse 2 reveals the vulnerability beneath it.',
    'Use a bridge with alternating lines that could be traded by two singers.',
    'Candlelit balconies, red fabric, summer thunder, close footsteps, a door left open.',
    'Use clean rhythmic stresses and authentic language; draft each bilingual line for meaning, not rhyme alone.',
    'Percussion Intro - Verse 1 - Pre-Chorus - Chorus - Dance Break - Verse 2 - Chorus - Duet Bridge - Final Chorus'
  ),
  template(
    '36-dancehall-main-character.txt',
    'Own the Night',
    'Dancehall',
    'Bold, playful, self-possessed',
    '94-104 BPM',
    "The singer enters a party with no need for approval and sets the room's energy.",
    'Use a commanding title phrase followed by a compact response suitable for a crowd.',
    'Verse 1 establishes style and presence; verse 2 turns attention into a shared dance challenge.',
    'The bridge should switch cadence and give the selector space to answer.',
    'Doorway pause, camera flashes, bass bins, bright fabric, circles opening on the floor.',
    'Prioritize rhythmic clarity, original boasts, and culturally grounded phrasing without forced slang.',
    'Intro Call - Verse 1 - Hook - Dance Break - Verse 2 - Hook - Cadence Switch - Final Hook - Outro Call'
  ),
  template(
    '37-country-pop-wildflower.txt',
    'Wildflower Weather',
    'Country Pop',
    'Bright, independent, romantic',
    '106-116 BPM',
    'Love works because neither person asks the other to become smaller.',
    'Build the chorus around wildflowers growing best where they have room.',
    'Verse 1 contrasts past control with present ease; verse 2 shows mutual support in action.',
    'The bridge names freedom as the reason commitment now feels possible.',
    'Roadside flowers, pickup dust, loose hair, fence lines, rain over open fields.',
    'Mix country specificity with a polished pop hook; keep the central metaphor consistent.',
    'Verse 1 - Pre-Chorus - Chorus - Verse 2 - Pre-Chorus - Chorus - Bridge - Final Chorus - Tag'
  ),
  template(
    '38-synth-pop-electric-heart.txt',
    'Electric Heart',
    'Synth-pop',
    'Glossy, vulnerable, urgent',
    '116-124 BPM',
    'A guarded person feels their emotional defenses fail in real time.',
    'Use a pulsing hook that contrasts mechanical control with unmistakably human feeling.',
    'Verse 1 describes careful distance; verse 2 records physical signs that the plan is failing.',
    'The bridge should briefly remove technology imagery and state what the singer wants.',
    'LED clocks, crossed wires, elevator hum, fingertips, power returning after a blackout.',
    'Use precise electronic imagery, punchy stressed syllables, and a chorus that climbs melodically.',
    'Synth Intro - Verse 1 - Pre-Chorus - Chorus - Post-Chorus - Verse 2 - Chorus - Bridge - Final Chorus'
  ),
  template(
    '39-alternative-rnb-distance.txt',
    'The Space Between Replies',
    'Alternative R&B',
    'Minimal, anxious, intimate',
    '64-74 BPM',
    "A relationship's uncertainty is measured through pauses, omissions, and delayed messages.",
    'Build the refrain around what silence communicates without claiming certainty about it.',
    'Verse 1 watches a conversation stall; verse 2 admits the singer also withholds the truth.',
    'Use an abstract bridge of fragmented thoughts that resolves into one complete sentence.',
    'Typing dots, ceiling shadows, airplane mode, half-charged phones, blue dawn.',
    'Use asymmetrical line lengths, slant rhyme, and negative space; resist explaining every image.',
    'Texture Intro - Verse 1 - Refrain - Verse 2 - Refrain - Fragment Bridge - Final Refrain - Dissolve'
  ),
  template(
    '40-indie-folk-family-roots.txt',
    'What the House Remembered',
    'Indie folk',
    'Tender, observant, intergenerational',
    '78-92 BPM',
    'An old family home becomes the narrator of departures, returns, and inherited habits.',
    'Let the refrain name one thing the house remembers that the family has forgotten.',
    'Verse 1 watches one generation leave; verse 2 sees another return carrying familiar gestures.',
    'The bridge reveals the house will outlive ownership but not necessarily memory.',
    'Pencil marks on a doorframe, floorboard creaks, garden tools, wallpaper seams, winter pipes.',
    'Use restrained personification, irregular rhyme, and details that imply history without summarizing it.',
    'Fingerpicked Intro - Verse 1 - Refrain - Verse 2 - Refrain - Instrumental Turn - Bridge - Final Refrain'
  ),
  template(
    '41-melodic-hip-hop-letter-to-you.txt',
    'Letter I Never Sent',
    'Melodic hip hop',
    'Reflective, remorseful, melodic',
    '78-88 BPM',
    'An unsent letter explains a breakup with more honesty than the original conversation allowed.',
    'Sing a hook about writing for closure without expecting a reply.',
    'Verse 1 owns specific mistakes; verse 2 recognizes what the other person may have experienced.',
    'Switch to half-sung delivery for a final passage that chooses not to send the letter.',
    'Crossed-out lines, desk-lamp light, an unsealed envelope, old address, morning bin.',
    'Use detailed rap verses, a simple melodic hook, and internal rhyme that never obscures accountability.',
    'Intro - Verse 1 (16 bars) - Melodic Hook - Verse 2 (16 bars) - Hook - Half-Sung Bridge - Final Hook - Outro'
  ),
  template(
    '42-melodic-hip-hop-turning-point.txt',
    'Corner Turned',
    'Melodic hip hop',
    'Restless, hopeful, self-aware',
    '86-96 BPM',
    'A small disciplined choice marks the beginning of a larger personal recovery.',
    'Make the hook celebrate direction, not arrival.',
    'Verse 1 describes cycles that kept repeating; verse 2 shows one ordinary day handled differently.',
    'Use a bridge that lists future goals but ends by returning attention to today.',
    'Unmade bed, running shoes, deleted numbers, a sunrise bus, checked boxes.',
    'Balance conversational rap with melodic line endings; keep growth believable and unfinished.',
    'Verse 1 - Hook - Verse 2 - Hook - Bridge - Short Verse 3 - Final Hook'
  ),
  template(
    '43-dream-pop-weightless.txt',
    'Weightless',
    'Dream pop',
    'Ethereal, romantic, suspended',
    '82-96 BPM',
    'A fleeting connection makes time feel briefly detached from ordinary life.',
    'Write a chorus of soft, image-led phrases that orbit the title rather than explain it.',
    'Verse 1 dissolves the setting into sensation; verse 2 notices time returning.',
    'The bridge should feel like waking: details sharpen and one difficult truth appears.',
    'Dust in projector light, underwater voices, slow curtains, pale moons, drifting hair.',
    'Use assonance, soft consonants, and suggestive images; avoid literal exposition in the chorus.',
    'Wash Intro - Verse 1 - Refrain - Instrumental Bloom - Verse 2 - Refrain - Waking Bridge - Final Refrain'
  ),
  template(
    '44-dream-pop-faded-photograph.txt',
    'Faded Photograph',
    'Dream pop',
    'Nostalgic, hazy, accepting',
    '76-88 BPM',
    'An old photograph triggers affection for a life that no longer fits.',
    'Use the faded image as proof that losing detail does not erase meaning.',
    'Verse 1 enters the photograph; verse 2 returns to the present and sees what has changed.',
    'The bridge names one detail the camera missed but memory retained.',
    'Sun-bleached paper, unknown handwriting, summer grass, soft focus, a bent corner.',
    'Repeat colors and textures, use loose rhyme, and let chronology blur without becoming confusing.',
    'Ambient Intro - Verse 1 - Chorus - Verse 2 - Chorus - Instrumental Drift - Bridge - Final Chorus - Fade'
  ),
  template(
    '45-cinematic-pop-rise-again.txt',
    'Rise Again',
    'Cinematic pop',
    'Inspiring, personal, expansive',
    '88-104 BPM',
    'Recovery is framed as a series of quiet choices before it becomes a visible victory.',
    'Start the chorus in first person and widen it to "we" by the final repetition.',
    'Verse 1 captures the private low; verse 2 introduces the first action that changed momentum.',
    'The bridge should strip to piano and name the fear that still remains.',
    'Floor-level sunlight, trembling knees, reopened curtains, footsteps on stairs.',
    'Earn the anthem through detail; delay broad declarations until the listener has seen the struggle.',
    'Piano Intro - Verse 1 - Pre-Chorus - Chorus - Verse 2 - Chorus - Quiet Bridge - Build - Final Chorus - Coda'
  ),
  template(
    '46-cinematic-pop-final-goodbye.txt',
    'The Kindest Goodbye',
    'Cinematic pop',
    'Heartbroken, mature, sweeping',
    '72-86 BPM',
    'Two people end a meaningful relationship without rewriting it as a failure.',
    'Make the chorus release the person while protecting what was true.',
    'Verse 1 takes in the final room; verse 2 imagines separate futures with tenderness.',
    'Use a bridge where both gratitude and grief fit into the same sentence.',
    'Packed books, two keys on a table, winter coats, elevator doors, separate taxis.',
    'Avoid blame language, build dynamic scale gradually, and change the last title line to signal acceptance.',
    'String Intro - Verse 1 - Chorus - Verse 2 - Chorus - Bridge - Orchestral Lift - Final Chorus - Piano Coda'
  ),
  template(
    '47-deep-house-ocean-drive.txt',
    'Ocean Drive at Dawn',
    'Deep house',
    'Cool, nocturnal, freeing',
    '118-122 BPM',
    'A solitary drive after a long night creates space for a difficult decision.',
    'Use a low, repeatable refrain about the road clearing as the mind clears.',
    'Verse 1 carries the unresolved night; verse 2 reaches the shoreline and chooses a direction.',
    'The breakdown should reduce the lyric to a breath, a place name, and the title phrase.',
    'Empty lanes, salt air, dashboard blue, shuttered hotels, silver water.',
    'Keep lyrics minimal, understated, and rhythmically even; let production carry the emotional peak.',
    'DJ Intro - Verse Fragment - Refrain - Groove - Verse Fragment - Refrain - Breakdown - Peak - Final Refrain'
  ),
  template(
    '48-deep-house-sunrise.txt',
    'Stay Until Sunrise',
    'Deep house',
    'Warm, hypnotic, intimate',
    '120-124 BPM',
    'Two people delay the end of a night because ordinary time resumes at sunrise.',
    'Build a four-line refrain around staying, light changing, and one honest request.',
    'Verse 1 notices the party thinning; verse 2 creates a private world inside the remaining music.',
    'Use the breakdown for a whispered countdown that stops before zero.',
    'Condensation, stacked glasses, pale windows, tired speakers, slow-moving shadows.',
    'Use economical language, gentle consonants, and small lyrical variations across repeated refrains.',
    'Intro - Verse Fragment - Refrain - Groove - Verse Fragment - Refrain - Countdown Breakdown - Final Groove - Refrain'
  ),
  template(
    '49-lofi-rain-window.txt',
    'Rain Window',
    'Lo-Fi',
    'Calm, lonely, meditative',
    '68-78 BPM',
    'A quiet room and steady rain create permission to stop solving everything for one evening.',
    'Write a soft seven-word refrain that can loop without sounding like a command.',
    'Use six short observational lines instead of conventional verses, moving from outside the window inward.',
    'Add one spoken line near the end that accepts uncertainty.',
    'Window trails, radiator clicks, tea steam, unread books, distant tires.',
    'Use plain language, no forced rhyme, and enough silence for ambient texture and beat variation.',
    'Rain Intro - Observation Set A - Refrain - Beat Passage - Observation Set B - Spoken Line - Refrain - Tape Stop'
  ),
  template(
    '50-pop-dance-floor-reset.txt',
    'Start Again Tonight',
    'Pop',
    'Euphoric, restorative, social',
    '120-128 BPM',
    'Friends use one joyful night to interrupt a season of isolation and overthinking.',
    'Create a chorus that treats dancing as a reset, not an escape from reality.',
    'Verse 1 resists going out; verse 2 discovers connection returning in small physical details.',
    'The bridge should acknowledge tomorrow still has problems while choosing joy now.',
    'Hallway mirror, shared taxi, coat-check number, ringing floorboards, dawn food stop.',
    'Use direct modern language, compact verse lines, and a final chorus with a communal vocal response.',
    'Intro - Verse 1 - Pre-Chorus - Chorus - Post-Chorus - Verse 2 - Chorus - Bridge - Final Chorus - Post-Chorus'
  ),
];

function renderLyricTemplate(seed: LyricTemplateSeed): string {
  return [
    'LYRIXSA PREMIUM LYRIC STARTER',
    '================================',
    '',
    `TEMPLATE: ${seed.title}`,
    `GENRE: ${seed.genre}`,
    `MOOD: ${seed.mood}`,
    `TEMPO: ${seed.tempo}`,
    '',
    'WRITING BRIEF',
    '-------------',
    `Core idea: ${seed.coreIdea}`,
    `Hook direction: ${seed.hookDirection}`,
    `Verse arc: ${seed.verseArc}`,
    `Contrast section: ${seed.contrastSection}`,
    `Image palette: ${seed.imagery}`,
    `Craft direction: ${seed.craftDirection}`,
    '',
    'SONG MAP',
    '--------',
    `Suggested structure: ${seed.structure}`,
    '',
    '[INTRO]',
    '- Establish one sound, image, or phrase. Do not explain the story yet.',
    '- Opening image: [____________________________________________]',
    '',
    '[VERSE 1]',
    '- Begin inside a specific moment with a person, place, object, and active verb.',
    '- First concrete detail: [_____________________________________]',
    '- What is happening beneath the surface: [_____________________]',
    '- Turn line into the next section: [____________________________]',
    '',
    '[PRE-CHORUS / LIFT]',
    '- Tighten the language and increase emotional pressure.',
    '- The question or realization: [_______________________________]',
    '',
    '[CHORUS / REFRAIN]',
    '- State the central feeling in language a listener can remember after one play.',
    '- Setup line: [________________________________________________]',
    '- Title or hook line: [________________________________________]',
    '- Emotional payoff: [__________________________________________]',
    '- Optional repeat or response: [_______________________________]',
    '',
    '[VERSE 2]',
    '- Move time, place, or perspective forward. Do not simply restate verse 1.',
    '- New evidence or complication: [______________________________]',
    '- Detail that changes the hook meaning: [______________________]',
    '',
    '[BRIDGE / CONTRAST]',
    '- Reveal the truth the earlier sections avoided.',
    '- Contrast line: [_____________________________________________]',
    '- Decision, admission, or unanswered question: [______________]',
    '',
    '[FINAL CHORUS / OUTRO]',
    '- Change one important word, pronoun, or image to show movement.',
    '- Final variation: [___________________________________________]',
    '- Last image or title tag: [___________________________________]',
    '',
    'PREMIUM FINISH CHECK',
    '--------------------',
    '[ ] The title appears at the strongest melodic moment.',
    '[ ] Each verse adds new information.',
    '[ ] Abstract feelings are supported by concrete images.',
    '[ ] Repeated lines gain meaning or intensity.',
    '[ ] The lyric sounds natural when spoken aloud.',
    '[ ] Familiar phrases have been replaced with personal detail.',
    '[ ] The final section shows a clear emotional change.',
    '',
    'Originality note: This is a writing framework, not a finished lyric. Replace every bracketed field with your own language and lived details.',
    '',
  ].join('\n');
}

export const LYRIC_TEMPLATE_FILES: BonusTextFile[] = LYRIC_TEMPLATE_SEEDS.map((seed) => ({
  fileName: seed.fileName,
  mimeType: 'text/plain',
  contents: renderLyricTemplate(seed),
}));

export const LYRIC_TEMPLATE_GENRES = [...new Set(LYRIC_TEMPLATE_SEEDS.map((seed) => seed.genre))];
